import { fn } from '@app/utils';
import { apiEndpoint, apiEndpoints } from '@app/utils/constants';
import axios from 'axios';
import _ from 'lodash';
import { refreshToken } from '@app/hooks/auth/useAuthActions';

const api = axios.create({
    baseURL: apiEndpoint,
    headers: {
        'Content-type': 'application/json',
    },
});

// Add the Authorisation token
api.interceptors.request.use(function (config) {
    const impersonationToken = fn.getImpersonationToken();
    const token = fn.getAccessToken();
    const tokenToUse = impersonationToken || token;
    if (tokenToUse) {
        config.headers['authorization'] = `Bearer ${tokenToUse}`;
    }
    return config;
});

// Refresh an expired Authorisation token?
api.interceptors.response.use(
    async (response) => {
        return response;
    },
    async (error) => {
        if (error.response) {
            const originalRequest = error.config;
            const accessTokenExpiry = fn.getAccessTokenExpiry();
            const refreshTokenValue = fn.getRefreshToken();
            const shouldRefreshToken =
                accessTokenExpiry && accessTokenExpiry < Date.now() && refreshTokenValue;

            // Has the access token expired?
            if (
                shouldRefreshToken &&
                error.response.status === 401 &&
                originalRequest.url !== apiEndpoints.auth.refreshToken
            ) {
                // Update tokens and re-run the original request
                try {
                    const refresh = await refreshToken({ refreshToken: refreshTokenValue });
                    const expiresIn = refresh?.expires_in || 3600; // Defaults to 60 mins
                    if (refresh?.token) {
                        fn.storeAccessToken(refresh?.token, {
                            lifespan: expiresIn,
                        });
                        fn.storeRefreshToken(refresh?.refresh_token, {
                            lifespan: expiresIn,
                        });
                        return api(originalRequest);
                    }
                } catch {
                    // Let React Query handle the error
                }
            }
        }

        // Throw error if it is valid
        throw error;
    }
);

export const getApiErrors = (
    errors?: string | string[] | Record<string, string[]>,
    single = false
) => {
    if (_.isString(errors)) {
        return errors;
    }

    if (_.isArray(errors)) {
        if (single) {
            return errors?.[0];
        }
        return errors.join('\r\n');
    }

    if (_.isObject(errors)) {
        const flattenedErrors = _.map(errors, (errorsArray) => {
            return errorsArray;
        });
        if (single) {
            return flattenedErrors?.[0];
        }
        return flattenedErrors.join('\r\n');
    }

    return false;
};

export default api;
